import React, { useState, useCallback, useMemo, memo, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { Box, Stack, Typography, Skeleton } from "@mui/material";
const isDateStringformat = (value) => {
  if (typeof value !== "string") return false;

  const date = new Date(value);
  return !isNaN(date.getTime());
};
const formatYValue = (value) => {
  if (value == null || value === "") return "";

  // 👉 Handle date first
  if (isDateStringformat(value)) {
    const date = new Date(value);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  const num = Number(value);
  if (isNaN(num)) return value;

  // 👉 Remove commas
  return Number.isInteger(num)
    ? String(num)
    : num.toFixed(2).replace(/\.00$/, ""); // removes .00 if not needed
};
const isDateString = (value) => {
  if (typeof value !== "string") return false;
  // Only treat as date if it matches common date patterns (contains - or / with digits)
  if (!/\d{2,4}[-/]\d{1,2}[-/]\d{1,4}/.test(value)) return false;
  const date = new Date(value);
  return !isNaN(date);
};

const formatXValue = (value) => {
  if (value == null || value === "") return "";
  if (isDateString(value)) {
    const date = new Date(value);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    });
  }
  const str = String(value).replace(/,/g, "");
  if (/^\d+$/.test(str)) {
    return String(Number(str));
  }
  return str.length > 12 ? str.slice(0, 12) + "…" : str;
};

const formatLabel = (key) => {
  if (!key) return "";
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const CustomLegendItem = memo(({ color, label, dashed }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Box display="flex" alignItems="center">
      <Box
        sx={{
          width: 16,
          borderTop: dashed ? `2px dashed ${color}` : `2px solid ${color}`,
        }}
      />
      <Box
        sx={{ width: 8, height: 8, background: color, borderRadius: "50%" }}
      />
      <Box
        sx={{
          width: 16,
          borderTop: dashed ? `2px dashed ${color}` : `2px solid ${color}`,
        }}
      />
    </Box>
    <Typography fontSize={12} color="#4D5F6E" fontWeight={500}>
      {label}
    </Typography>
  </Stack>
));

const CustomTooltipContent = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const dataPoint = payload[0]?.payload || {};

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E8ECF0",
        borderRadius: "8px",
        p: "10px 14px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        maxWidth: 300,
      }}
    >
      {Object.entries(dataPoint).map(([key, value]) => (
        <Box
          key={key}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            py: 0.25,
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#4D5F6E" }}>
            {formatLabel(key)}
          </Typography>

          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "#1A2025",
            }}
          >
            {typeof value === "number" ? formatYValue(value) : value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const ChartSkeleton = () => (
  <Box>
    <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
    <Stack direction="row" spacing={3} justifyContent="center" mt={2}>
      {[1, 2].map((i) => (
        <Stack key={i} direction="row" spacing={1} alignItems="center">
          <Skeleton variant="circular" width={10} height={10} />
          <Skeleton width={80} height={16} />
        </Stack>
      ))}
    </Stack>
  </Box>
);

const ChartContent = memo(
  ({
    loading,
    chartData,
    chartWidth,
    lines,
    legendItems,
    computedDomain,
    hasThreshold,
    threshold,
    thresholdColor,
    xKey,
  }) => {
    const safeXKey = xKey || "name";
    if (loading) return <ChartSkeleton />;
    return (
      <>
        <Box
          sx={{
            overflowX: chartWidth > 0 ? "auto" : "hidden",
            overflowY: "hidden",
            width: "100%",
            pb: 1,
          }}
        >
          <Box
            sx={{
              width: "100%",
              minWidth: chartWidth,
              height: { sm: 160, md: 200, lg: 300 },
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 12, right: 24, left: 8, bottom: 4 }}
              >
                <CartesianGrid
                  stroke="#E5E7EB"
                  strokeDasharray="4 4"
                  vertical
                  horizontal={false}
                />
                <XAxis
                  dataKey={safeXKey}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#4D5F6E", fontSize: 12, fontWeight: 500 }}
                  tickFormatter={formatYValue}
                  interval="preserveStartEnd"
                  height={36}
                  tickMargin={8}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#4D5F6E", fontSize: 12, fontWeight: 500 }}
                  tickFormatter={formatXValue}
                  width={85}
                  tickMargin={4}
                  domain={computedDomain}
                  tickCount={6}
                />
                <Tooltip content={<CustomTooltipContent />} />

                {hasThreshold && (
                  <ReferenceLine
                    y={Number(threshold)}
                    stroke={thresholdColor}
                    strokeDasharray="6 4"
                    strokeWidth={2}
                    label={{
                      value: `Threshold: ${formatYValue(threshold)}`,
                      position: "insideTopRight",
                      fill: thresholdColor,
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  />
                )}

                {lines.map((line, index) => (
                  <Line
                    key={index}
                    type="monotone"
                    dataKey={line.dataKey}
                    stroke={line.color}
                    strokeWidth={2}
                    strokeDasharray={line.dashed ? "5 4" : undefined}
                    dot={{
                      r: 4,
                      fill: line.color,
                      stroke: "#fff",
                      strokeWidth: 2,
                    }}
                    activeDot={{ r: 6 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "12px 24px",
            pt: 1.5,
            pb: 0.5,
          }}
        >
          {xKey && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "3px",
                  bgcolor: "#667F93",
                }}
              />
              <Typography fontSize={12} color="#4D5F6E" fontWeight={500}>
                {formatLabel(xKey)} (X-axis)
              </Typography>
            </Stack>
          )}

          {/* Y-axis Label */}
          {lines.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "3px",
                  bgcolor: lines[0].color || "#3B82F6",
                }}
              />
              <Typography fontSize={12} color="#4D5F6E" fontWeight={500}>
                {lines.length === 1
                  ? formatLabel(lines[0].dataKey) + " (Y-axis)"
                  : "Values (Y-axis)"}
              </Typography>
            </Stack>
          )}
          {legendItems.map((item, index) => (
            <CustomLegendItem key={index} {...item} />
          ))}
        </Box>
      </>
    );
  },
);

export default function MultiLineChart({
  onRefresh,
  chartData = [],
  lines = [],
  legendItems = [],
  domain,
  refreshTrigger,
  threshold,
  thresholdColor = "#FF4D4F",
  xKey,
}) {
  const [loading, setLoading] = useState(false);
  const formattedData = useMemo(() => {
    return chartData.map((item) => {
      const updatedItem = { ...item };

      Object.keys(updatedItem).forEach((key) => {
        let value = updatedItem[key];

        if (typeof value === "string") {
          if (value === "N/A") {
            updatedItem[key] = null; // better than 0 for charts
          } else if (value.includes("%")) {
            const num = parseFloat(value.replace("%", ""));
            updatedItem[key] = isNaN(num) ? null : num;
          }
        }
      });

      return updatedItem;
    });
  }, [chartData]);
  const chartWidth = useMemo(
    () => formattedData.length * 80,
    [formattedData.length],
  );

  // Use passed domain if provided, otherwise compute from data
  const computedDomain = useMemo(() => {
    if (Array.isArray(domain) && domain.length === 2) return domain;

    const lineKeys = lines.map((l) => l.dataKey);
    let min = Infinity;
    let max = -Infinity;

    formattedData.forEach((item) => {
      // FIX
      lineKeys.forEach((key) => {
        const val = Number(item[key]);
        if (!isNaN(val)) {
          if (val < min) min = val;
          if (val > max) max = val;
        }
      });
    });

    if (min === Infinity) return [0, 100];

    const padding = (max - min) * 0.1 || max * 0.1 || 10;
    return [Math.max(0, Math.floor(min - padding)), Math.ceil(max + padding)];
  }, [formattedData, lines, domain]);

  const hasThreshold = threshold != null && !isNaN(Number(threshold));

  const handleRefreshClick = useCallback(() => {
    setLoading(true);
    onRefresh?.();
    setTimeout(() => setLoading(false), 1500);
  }, [onRefresh]);

  useEffect(() => {
    if (refreshTrigger) {
      handleRefreshClick();
    }
  }, [refreshTrigger, handleRefreshClick]);

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: 2,
        height: "100%",
      }}
    >
      <ChartContent
        loading={loading}
        chartData={formattedData}
        chartWidth={chartWidth}
        lines={lines}
        legendItems={legendItems}
        computedDomain={computedDomain}
        hasThreshold={hasThreshold}
        threshold={threshold}
        thresholdColor={thresholdColor}
        xKey={xKey}
      />
    </Box>
  );
}